
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * BlockedIPFiltered struct
 */
class BlockedIPFiltered {
  /**
   * Initialize struct
   * @param {!Array=} iPv4Address
   * @param {!number=} encounteredTimes
   * @constructor
   */
  constructor (iPv4Address = [], encounteredTimes = new UInt64(0, 0)) {
    this.IPv4Address = iPv4Address
    this.EncounteredTimes = encounteredTimes
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BlockedIPFiltered}
   * @param {!BlockedIPFiltered} other Other struct
   * @returns {!BlockedIPFiltered} This struct
   */
  copy (other) {
    if (other.IPv4Address != null) {
      this.IPv4Address = []
      for (let item of other.IPv4Address) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.IPv4Address.push(tempItem)
        } else {
          this.IPv4Address.push(null)
        }
      }
    } else {
      this.IPv4Address = null
    }
    if (other.EncounteredTimes != null) {
      this.EncounteredTimes = UInt64.fromNumber(other.EncounteredTimes)
    } else {
      this.EncounteredTimes = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BlockedIPFiltered}
   * @returns {!BlockedIPFiltered} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BlockedIPFilteredModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BlockedIPFilteredModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BlockedIPFiltered}
   * @param {!BlockedIPFiltered} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BlockedIPFiltered)) {
      throw new TypeError('Instance of BlockedIPFiltered is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BlockedIPFiltered}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      IPv4Address: ((this.IPv4Address != null) ? Array.from(this.IPv4Address, item => ((item != null) ? item : null)) : null),
      EncounteredTimes: ((this.EncounteredTimes != null) ? this.EncounteredTimes.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BlockedIPFiltered.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BlockedIPFiltered} other Object value
   * @returns {!BlockedIPFiltered} Created struct
   */
  static fromObject (other) {
    return new BlockedIPFiltered().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BlockedIPFiltered}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BlockedIPFiltered.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BlockedIPFiltered}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 13017
  }
}

export { BlockedIPFiltered };

/**
 * Fast Binary Encoding BlockedIPFiltered field model
 */
class FieldModelBlockedIPFiltered extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.IPv4Address = new fbe.FieldModelArray(new fbe.FieldModelByte(buffer, 4 + 4), buffer, 4 + 4, 4)
    this.EncounteredTimes = new fbe.FieldModelUInt64(buffer, this.IPv4Address.fbeOffset + this.IPv4Address.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.IPv4Address.fbeSize + this.EncounteredTimes.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.IPv4Address.fbeExtra + this.EncounteredTimes.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBlockedIPFiltered.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 13017
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IPv4Address.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IPv4Address.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Address.fbeSize

    if ((fbeCurrentSize + this.EncounteredTimes.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EncounteredTimes.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EncounteredTimes.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!BlockedIPFiltered} fbeValue Default value, defaults is new BlockedIPFiltered()
   * @returns {!BlockedIPFiltered} BlockedIPFiltered value
   */
  get (fbeValue = new BlockedIPFiltered()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!BlockedIPFiltered} fbeValue BlockedIPFiltered value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IPv4Address.fbeSize) <= fbeStructSize) {
      this.IPv4Address.get(fbeValue.IPv4Address)
    } else {
      fbeValue.IPv4Address.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Address.fbeSize

    if ((fbeCurrentSize + this.EncounteredTimes.fbeSize) <= fbeStructSize) {
      fbeValue.EncounteredTimes = this.EncounteredTimes.get()
    } else {
      fbeValue.EncounteredTimes = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EncounteredTimes.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBlockedIPFiltered}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBlockedIPFiltered}
   * @param {!BlockedIPFiltered} fbeValue BlockedIPFiltered value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBlockedIPFiltered}
   * @param {BlockedIPFiltered} fbeValue BlockedIPFiltered value
   */
  setFields (fbeValue) {
    this.IPv4Address.set(fbeValue.IPv4Address)
    this.EncounteredTimes.set(fbeValue.EncounteredTimes)
  }
}

export { FieldModelBlockedIPFiltered };

/**
 * Fast Binary Encoding BlockedIPFiltered model
 */
class BlockedIPFilteredModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBlockedIPFiltered(this.buffer, 4)
  }

  /**
   * Get the BlockedIPFiltered model
   * @this {!BlockedIPFilteredModel}
   * @returns {!FieldModelBlockedIPFiltered} model BlockedIPFiltered model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BlockedIPFilteredModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BlockedIPFilteredModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BlockedIPFilteredModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BlockedIPFilteredModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBlockedIPFiltered.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BlockedIPFilteredModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BlockedIPFilteredModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BlockedIPFilteredModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BlockedIPFilteredModel}
   * @param {!BlockedIPFiltered} value BlockedIPFiltered value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BlockedIPFilteredModel}
   * @param {!BlockedIPFiltered} value BlockedIPFiltered value, defaults is new BlockedIPFiltered()
   * @return {!object} Deserialized BlockedIPFiltered value and its size
   */
  deserialize (value = new BlockedIPFiltered()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BlockedIPFiltered(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BlockedIPFiltered(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BlockedIPFilteredModel}
   * @param {!number} prev Previous BlockedIPFiltered model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BlockedIPFilteredModel };
